import { useState } from "react";

import { Controller, useForm } from "react-hook-form";

import { type PatchMyUserRequestBody, type Session } from "@eisox/api/core-api";
import { useAuthContext } from "@eisox/auth";
import {
  toast,
  ActionButton,
  Alert,
  Button,
  Divider,
  IconButton,
  Page,
  PasswordInput,
  PhoneInput,
  Sidebar,
  TextField,
  Typography,
} from "@eisox/design-system";
import { ArrowRightIcon, CheckedIcon, PanelLeftIcon, RejectIcon } from "@eisox/icons";
import { colors } from "@eisox/tokens";
import { useMediaQuery } from "@eisox/tools";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";

import { accountApi } from "~/api";
import { FieldContainer } from "~/components/field-container";
import { Help } from "~/components/help";
import { m } from "~/paraglide/messages";
import { getDirtyValues } from "~/utils/form.utils";

import { accountSchema, changeEmailSchema, type AccountSchemaType, type ChangeEmailSchemaType } from "../helpers";
import { PhoneNumberValidationDialog } from "./phone-number-validation-dialog";

interface MyAccountPageProps {
  user: Session;
}

const NAME = "MyAccountPage";

export const MyAccountPage: React.FC<MyAccountPageProps> = ({ user }) => {
  const isMobile = useMediaQuery("(max-width: 768px)");

  const { refreshSession, signOut } = useAuthContext(NAME);

  const generalForm = useForm<AccountSchemaType>({
    criteriaMode: "all",
    resolver: zodResolver(accountSchema),
    values: {
      firstName: user.firstName ?? "",
      lastName: user.lastName ?? "",
      company: user.company ?? "",
      phoneNumber: user.phoneNumber ?? "",
    },
  });

  const emailForm = useForm<ChangeEmailSchemaType>({
    criteriaMode: "all",
    resolver: zodResolver(changeEmailSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const [phoneNumberValidationDialogOpen, setPhoneNumberValidationDialogOpen] = useState(false);

  const { mutate: saveGeneral } = useMutation({
    meta: { toast: { success: m.my_account_general_success() } },
    mutationFn: (data: PatchMyUserRequestBody) => accountApi.updateProfile(data),
    onSuccess: async (_, { phoneNumber }) => {
      await refreshSession();
      // A new number is saved unverified and already texted, so collect the code straight away.
      if (phoneNumber) {
        toast.success(m.code_send());
        setPhoneNumberValidationDialogOpen(true);
      }
    },
  });

  const { mutate: saveEmail } = useMutation({
    meta: { toast: { success: m.my_account_email_success() } },
    mutationFn: (data: ChangeEmailSchemaType) =>
      accountApi.updateProfile({ email: data.email, currentPassword: data.password }),
    // The server drops every session on an email change, so this one is already dead.
    onSuccess: async () => {
      await signOut();
    },
  });

  const { mutate: changePassword, isPending: isChangePasswordPending } = useMutation({
    meta: { toast: { success: m.my_account_password_success() } },
    mutationFn: (email: string) => accountApi.requestPasswordReset(email),
  });

  const { mutate: sendValidationPhoneNumberMutation } = useMutation({
    meta: { toast: { success: m.code_send(), error: m.send_code_error() } },
    mutationFn: () => accountApi.requestPhoneValidation(),
    onSuccess: () => setPhoneNumberValidationDialogOpen(true),
  });

  const onSubmitGeneral = generalForm.handleSubmit((data: AccountSchemaType) => {
    const body = getDirtyValues(generalForm.formState.dirtyFields, data);
    saveGeneral(body);
  });

  const onSubmitChangeEmail = emailForm.handleSubmit((data: ChangeEmailSchemaType) => saveEmail(data));

  return (
    <Page.Root>
      <Page.Header>
        <div className="flex h-auto items-center">
          {isMobile && (
            <Sidebar.Trigger asChild>
              <IconButton>
                <PanelLeftIcon />
              </IconButton>
            </Sidebar.Trigger>
          )}
          <Page.Title>{m.my_acccount()}</Page.Title>
        </div>
      </Page.Header>
      <Page.Content className="p-2.5 md:p-10 overflow-y-auto">
        <div className="[&_h3]:font-medium gap-5 flex max-w-[700px] flex-col [&_h3]:text-md [&_h3]:text-primary-dark-gray">
          <div className="gap-2.5 flex flex-col">
            <Typography as="h3">{m.general()}</Typography>
            <div className="gap-2.5 md:grid-cols-2 grid grid-cols-1">
              <FieldContainer
                label={m.last_name()}
                error={generalForm.formState.errors["lastName"]}
                classNames={{ label: "text-primary-dark-gray" }}
              >
                <TextField.Root {...generalForm.register("lastName")} />
              </FieldContainer>
              <FieldContainer
                label={m.first_name()}
                error={generalForm.formState.errors["firstName"]}
                classNames={{ label: "text-primary-dark-gray" }}
              >
                <TextField.Root {...generalForm.register("firstName")} />
              </FieldContainer>
              <FieldContainer
                label={m.company()}
                error={generalForm.formState.errors["company"]}
                classNames={{ label: "text-primary-dark-gray" }}
              >
                <TextField.Root {...generalForm.register("company")} />
              </FieldContainer>
              <Controller
                control={generalForm.control}
                name="phoneNumber"
                render={({ field }) => (
                  <FieldContainer
                    label={m.phone()}
                    error={generalForm.formState.errors["phoneNumber"]}
                    classNames={{ label: "text-primary-dark-gray" }}
                  >
                    <div>
                      <div className="gap-2.5 flex items-center">
                        <PhoneInput.Input
                          {...field}
                          error={!!generalForm.formState.errors.phoneNumber}
                          countrySelectProps={{ disabled: true }}
                          defaultCountry="FR"
                          international={false}
                        />
                        {user.phoneVerified ? (
                          <CheckedIcon color={colors.primary.green} width={20} height={20} />
                        ) : (
                          <RejectIcon color={colors.primary.red} width={20} height={20} />
                        )}
                        <Help position="right">{m.phone_help()}</Help>
                      </div>
                      {!!user.phoneNumber && !user.phoneVerified && (
                        <Button onClick={() => sendValidationPhoneNumberMutation()}>
                          {m.verify_phone()}
                          <ArrowRightIcon />
                        </Button>
                      )}
                    </div>
                  </FieldContainer>
                )}
              />
            </div>
            <ActionButton
              disabled={!generalForm.formState.isDirty}
              onClick={e => {
                void onSubmitGeneral(e);
              }}
              className="ml-auto"
            >
              {m.save()}
              <ArrowRightIcon />
            </ActionButton>
          </div>
          <Divider className="my-0!" />
          <div className="gap-2.5 flex flex-col">
            <div className="gap-2.5 flex items-center">
              <Typography as="h3">{m.email_change()}</Typography> <Help position="right">{m.email_change_help()}</Help>
            </div>
            <div className="gap-2.5 md:grid-cols-2 grid grid-cols-1">
              <FieldContainer
                label={m.email()}
                error={emailForm.formState.errors["email"]}
                classNames={{ label: "text-primary-dark-gray" }}
              >
                <TextField.Root placeholder={user.email} {...emailForm.register("email")} />
              </FieldContainer>
              <FieldContainer
                label={m.password()}
                error={emailForm.formState.errors["password"]}
                classNames={{ label: "text-primary-dark-gray" }}
              >
                <PasswordInput {...emailForm.register("password")} />
              </FieldContainer>
            </div>
            <ActionButton
              disabled={!emailForm.formState.dirtyFields.email}
              onClick={e => {
                void onSubmitChangeEmail(e);
              }}
              className="ml-auto"
            >
              {m.save()}
              <ArrowRightIcon />
            </ActionButton>
            {emailForm.formState.dirtyFields.email && (
              <Alert className="col-span-2" severity="warning">
                {m.email_change_warning()}
              </Alert>
            )}
          </div>
          <Divider className="my-0!" />
          <div className="gap-2.5 flex flex-col items-start">
            <Typography as="h3">{m.password_change()}</Typography>
            <p className="text-base text-primary-dark-gray">{m.password_change_warning()}</p>
            <Button disabled={isChangePasswordPending} onClick={() => changePassword(user.email)}>
              {m.reset_password()} <ArrowRightIcon />
            </Button>
          </div>
        </div>
      </Page.Content>

      {phoneNumberValidationDialogOpen && (
        <PhoneNumberValidationDialog
          open={phoneNumberValidationDialogOpen}
          onOpenChange={setPhoneNumberValidationDialogOpen}
          onSuccess={() => void refreshSession()}
        />
      )}
    </Page.Root>
  );
};

MyAccountPage.displayName = NAME;
